import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserData, AuthResponse } from '../types';

interface AuthContextType {
  user: UserData | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  loading: boolean;
  profileImageUrl: string | null;
  login: (authResponse: AuthResponse) => void;
  logout: () => void;
  updateUser: (userData: UserData) => void;
  setEmailVerified: (verified: boolean) => void;
  refreshProfileImage: (pictureUrl?: string | null) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [profileImageObjectUrl, setProfileImageObjectUrl] = useState<string | null>(null);

  // Helper function to fetch and cache profile image
  const fetchAndCacheProfileImage = async (userData: UserData, pictureOverride?: string | null) => {
    const sourcePicture = pictureOverride ?? userData?.social_data?.picture;

    if (!sourcePicture) {
      if (profileImageObjectUrl) {
        URL.revokeObjectURL(profileImageObjectUrl);
        setProfileImageObjectUrl(null);
      }
      setProfileImageUrl(null);
      return;
    }

    if (profileImageObjectUrl) {
      URL.revokeObjectURL(profileImageObjectUrl);
      setProfileImageObjectUrl(null);
    }

    setProfileImageUrl(sourcePicture);
  };

  useEffect(() => {
    // Check for existing user data in localStorage on mount
    const initializeAuth = async () => {
      try {
        const currentPath = window.location.pathname;
        const currentParams = new URLSearchParams(window.location.search);
        const hasBindingTokenOnAuth = currentPath === '/auth' && currentParams.has('token');

        if (hasBindingTokenOnAuth) {
          // Force explicit auth when entering from a binding link to avoid account/token mismatch.
          localStorage.removeItem('user');
          localStorage.removeItem('access');
          localStorage.removeItem('refresh');
          localStorage.removeItem('emailVerified');
          setUser(null);
          setAccessToken(null);
          setRefreshToken(null);
          setIsEmailVerified(false);
          return;
        }

        const storedUser = localStorage.getItem('user');
        const accessToken = localStorage.getItem('access');
        const refreshToken = localStorage.getItem('refresh');
        const emailVerified = localStorage.getItem('emailVerified');
        
        // Only restore user if BOTH user data AND tokens exist
        if (storedUser && (accessToken || refreshToken)) {
          setUser(JSON.parse(storedUser));
          setAccessToken(accessToken);
          setRefreshToken(refreshToken);
          setIsEmailVerified(emailVerified === 'true');
        } else {
          // If we have user but no tokens, clear everything
          console.log('User data found but no tokens - clearing auth state');
          localStorage.removeItem('user');
          localStorage.removeItem('access');
          localStorage.removeItem('refresh');
          localStorage.removeItem('emailVerified');
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        // Clear potentially corrupted data
        localStorage.removeItem('user');
        localStorage.removeItem('access');
        localStorage.removeItem('refresh');
        localStorage.removeItem('emailVerified');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Fetch profile image when user is available (on mount after restore or after login)
  useEffect(() => {
    if (user && !profileImageUrl) {
      fetchAndCacheProfileImage(user);
    } else if (!user) {
      setProfileImageUrl(null);
    }
  }, [user?.id]);

  const login = (authResponse: AuthResponse) => {
    setUser(authResponse.user);
    setRefreshToken(authResponse.refresh);
    setAccessToken(authResponse.access);
    // Default to false, will be checked separately
    setIsEmailVerified(false);
    localStorage.setItem('user', JSON.stringify(authResponse.user));
    localStorage.setItem('access', authResponse.access);
    localStorage.setItem('refresh', authResponse.refresh);
    localStorage.setItem('emailVerified', 'false');
    fetchAndCacheProfileImage(authResponse.user);
  };

  const logout = () => {
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    setIsEmailVerified(false);
    setProfileImageUrl(null);
    if (profileImageObjectUrl) {
      URL.revokeObjectURL(profileImageObjectUrl);
      setProfileImageObjectUrl(null);
    }
    localStorage.removeItem('user');
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('emailVerified');
    localStorage.removeItem('profileImageUrl');
  };

  const updateUser = (userData: UserData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const refreshProfileImage = async (pictureUrl?: string | null) => {
    if (!user) {
      setProfileImageUrl(null);
      return;
    }
    await fetchAndCacheProfileImage(user, pictureUrl);
  };

  useEffect(() => {
    return () => {
      if (profileImageObjectUrl) {
        URL.revokeObjectURL(profileImageObjectUrl);
      }
    };
  }, [profileImageObjectUrl]);

  const setEmailVerified = (verified: boolean) => {
    setIsEmailVerified(verified);
    localStorage.setItem('emailVerified', verified.toString());
  };

  const value = {
    user,
    accessToken,
    refreshToken,
    isAuthenticated: !!user,
    isEmailVerified,
    loading,
    profileImageUrl,
    login,
    logout,
    updateUser,
    setEmailVerified,
    refreshProfileImage,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};